// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAppLogger } from '@/services/appLogger';
import { compareLocalDateStrings } from '@/services/localDate';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import {
  buildRoundMemberAddMessage,
  buildRoundMemberRemoveMessage,
  buildRoundRemoveMessage,
  buildRoundGroupsSwapMessage,
  buildTourMemberRemoveMessage,
  resolveMemberRemoveRejectedMessage,
} from '@/services/memberRemoval';
import { buildRoundUpdateMessage } from '@/services/roundUpdateMessage';

import type {
  TourRoundSummary,
  Course,
  GameDefinition,
  TourPlayerSnapshot,
  TourMemberSnapshot,
  ExistingPlayerRow,
  DraftPlayerRow,
  PlayerRow,
  TournamentGamesState,
  RoundGamesState,
  RoundGroupsData,
} from '../types';
import { MAX_PLAYERS, ACK_TIMEOUT_MS } from '../constants';
import { generateId, normalizeGameId } from '../utils';

export function useTourEditData(tourId: string | undefined) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const logToBff = useMemo(
    () => createAppLogger(apiBaseUrl, user?.token, { source: 'tour-edit' }),
    [apiBaseUrl, user?.token]
  );
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));

  // --- Tour state ---
  const [tourName, setTourName] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const lastTourNameRef = useRef('');

  // --- Tournament games ---
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [tournamentGamesState, setTournamentGamesState] = useState<TournamentGamesState>({
    gameIds: [],
    weightsByGameId: {},
  });

  // --- Rounds ---
  const [rounds, setRounds] = useState<TourRoundSummary[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundGamesByRoundId, setRoundGamesByRoundId] = useState<Record<string, RoundGamesState>>({});
  const [roundPlayerIdsByRoundId, setRoundPlayerIdsByRoundId] = useState<Record<string, string[]>>({});
  const [playerIdsWithScoresByRoundId, setPlayerIdsWithScoresByRoundId] = useState<Record<string, Set<string>>>({});
  const [roundGroupsByRoundId, setRoundGroupsByRoundId] = useState<Record<string, RoundGroupsData>>({});

  // --- Players ---
  const [availablePlayers, setAvailablePlayers] = useState<TourPlayerSnapshot[]>([]);
  const [playerRows, setPlayerRows] = useState<PlayerRow[]>([]);
  const [playerRowOpen, setPlayerRowOpen] = useState<string | null>(null);
  const [tourPlayersLoading, setTourPlayersLoading] = useState(false);
  const friendPlayerIdsRef = useRef<Set<string>>(new Set());
  const playersInitializedRef = useRef(false);

  // --- Courses ---
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseError, setCourseError] = useState<string | null>(null);


  // --- Pending refs ---
  const pendingRequestIdRef = useRef<string | null>(null);
  const pendingMemberAddRequestIdRef = useRef<string | null>(null);
  const pendingMemberAddPlayerIdRef = useRef<string | null>(null);
  const pendingTourRemoveRequestIdRef = useRef<string | null>(null);
  const pendingTourRemovePlayerIdRef = useRef<string | null>(null);
  const pendingRoundDeletesRef = useRef<Map<string, { roundId: string; timeout: ReturnType<typeof setTimeout> }>>(new Map());
  const pendingRoundAddRequestIdRef = useRef<string | null>(null);
  const pendingRoundAddRoundIdRef = useRef<string | null>(null);
  const pendingRoundRemoveRequestIdRef = useRef<string | null>(null);
  const pendingPayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingMemberAddPayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingTourRemovePayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingRoundDeletePayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingRoundAddPayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingRoundRemovePayloadRef = useRef<Record<string, unknown> | null>(null);
  const pendingRoundUpdatesRef = useRef<Record<string, unknown>>({});
  const pendingTournamentSyncRef = useRef<TournamentGamesState | null>(null);
  const pendingMemberAddDraftRowIdRef = useRef<string | null>(null);
  const ackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const memberAddAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tourRemoveAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // roundDeleteAckTimeoutRef removed — timeouts tracked in pendingRoundDeletesRef
  const roundAddAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roundRemoveAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSwapRequestIdRef = useRef<string | null>(null);
  const pendingSwapPayloadRef = useRef<Record<string, unknown> | null>(null);
  const swapAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconcileRefreshTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const memberAddReconcileTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const tourRemoveReconcileTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const pendingRoundMemberChangesRef = useRef<
    Record<string, { action: 'add' | 'remove'; roundId: string; playerId: string; hadRecordedScores: boolean }>
  >({});

  // --- Helpers ---
  const getDefaultWeightForGame = useCallback(
    (gameId: string) => {
      const match = gameDefinitions.find((game) => game.id === gameId);
      const fallback = match?.defaultWeightPercent;
      return typeof fallback === 'number' && !Number.isNaN(fallback) ? fallback : 100;
    },
    [gameDefinitions]
  );

  const buildWeightsMap = useCallback(
    (gameIds: string[], existing: Record<string, number> | undefined) => {
      const next: Record<string, number> = {};
      gameIds.forEach((id) => {
        if (!id) return;
        const weight = existing?.[id];
        next[id] = typeof weight === 'number' && !Number.isNaN(weight) ? weight : getDefaultWeightForGame(id);
      });
      return next;
    },
    [getDefaultWeightForGame]
  );

  const tournamentGameIdSet = useMemo(
    () => new Set(tournamentGamesState.gameIds.map((id) => normalizeGameId(id)).filter((id) => id.length > 0)),
    [tournamentGamesState.gameIds]
  );

  const isTournamentManagedGame = useCallback(
    (gameId: string) => tournamentGameIdSet.has(normalizeGameId(gameId)),
    [tournamentGameIdSet]
  );

  const enabledGameDefinitions = useMemo(
    () => gameDefinitions.filter((game) => (game.status ?? '').toLowerCase() === 'enabled'),
    [gameDefinitions]
  );

  const orderedRounds = useMemo(
    () =>
      [...rounds].sort((a, b) => {
        const playDateOrder = compareLocalDateStrings(a.playDate, b.playDate);
        if (playDateOrder !== 0) return playDateOrder;
        const nameOrder = (a.name ?? '').localeCompare(b.name ?? '');
        if (nameOrder !== 0) return nameOrder;
        return (a.id ?? '').localeCompare(b.id ?? '');
      }),
    [rounds]
  );

  const sortPlayersByFriendPriority = useCallback((players: TourPlayerSnapshot[]) => {
    const friendIds = friendPlayerIdsRef.current;
    return [...players].sort((a, b) => {
      const aFriend = a.isFriend === true || friendIds.has(a.id);
      const bFriend = b.isFriend === true || friendIds.has(b.id);
      if (aFriend !== bFriend) return aFriend ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, []);

  const tourMemberOptions = useMemo(
    () =>
      playerRows
        .filter((row): row is ExistingPlayerRow => row.kind === 'existing')
        .map((row) => ({ id: row.playerId, name: row.name })),
    [playerRows]
  );

  const playerNameById = useMemo(() => {
    const byId: Record<string, string> = {};
    availablePlayers.forEach((player) => {
      if (player.id) byId[player.id] = player.name;
    });
    playerRows.forEach((row) => {
      if (row.kind === 'existing' && row.playerId && row.name && !byId[row.playerId]) {
        byId[row.playerId] = row.name;
      }
      if (row.kind === 'draft' && row.selectedPlayerId && row.selectedPlayerName && !byId[row.selectedPlayerId]) {
        byId[row.selectedPlayerId] = row.selectedPlayerName;
      }
    });
    return byId;
  }, [availablePlayers, playerRows]);

  // --- Fetch functions ---
  const fetchTour = useCallback(
    async (options?: { silent?: boolean; preserveName?: boolean }) => {
      if (!tourId || !user?.token) return;
      const silent = options?.silent === true;
      const preserveName = options?.preserveName === true;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const response = await fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) throw new Error(`Failed to load tour (${response.status})`);
        const data = (await response.json()) as Record<string, unknown>;
        const incomingName =
          (data.tourName as string) ?? (data.TourName as string) ?? (data.name as string) ?? (data.Name as string) ?? '';
        // Background reconcile refetches must not overwrite the name field while
        // the user is editing it — that reverts characters they've just typed.
        if (!preserveName) {
          setTourName(incomingName);
          lastTourNameRef.current = incomingName.trim();
        }
        setIsHidden((data.isHidden as boolean | undefined) ?? (data.IsHidden as boolean | undefined) ?? false);

        const tournamentGameIdsRaw = [
          ...((data.gameDefinitionIds as string[]) ?? []),
          ...((data.GameDefinitionIds as string[]) ?? []),
        ];
        const seenIds = new Set<string>();
        const tournamentGameIds = tournamentGameIdsRaw.filter((id) => {
          const n = normalizeGameId(id);
          if (!n || seenIds.has(n)) return false;
          seenIds.add(n);
          return true;
        });

        const incomingWeights = [
          ...((data.gameWeights as Array<Record<string, unknown>>) ?? []),
          ...((data.GameWeights as Array<Record<string, unknown>>) ?? []),
        ];
        const parsedWeights: Record<string, number> = {};
        incomingWeights.forEach((entry) => {
          const gameDefinitionId =
            (entry.gameDefinitionId as string) ?? (entry.GameDefinitionId as string);
          const weightPercent =
            (entry.weightPercent as number) ?? (entry.WeightPercent as number);
          if (!gameDefinitionId || typeof weightPercent !== 'number' || Number.isNaN(weightPercent)) return;
          parsedWeights[gameDefinitionId] = weightPercent;
        });
        const normalizedWeights = buildWeightsMap(tournamentGameIds, parsedWeights);

        const pendingSync = pendingTournamentSyncRef.current;
        if (pendingSync) {
          // If we have a pending tournament sync, check if the fetched data matches
          const pendingSet = new Set(pendingSync.gameIds.map(normalizeGameId));
          const fetchedSet = new Set(tournamentGameIds.map(normalizeGameId));
          const sameSet = pendingSet.size === fetchedSet.size && [...pendingSet].every((id) => fetchedSet.has(id));
          if (!sameSet) return; // Defer — data hasn't converged yet
          pendingTournamentSyncRef.current = null;
        }

        setTournamentGamesState((prev) => {
          const prevByNormalized = new Map(prev.gameIds.map((id) => [normalizeGameId(id), id]));
          const fetchedByNormalized = new Map(tournamentGameIds.map((id) => [normalizeGameId(id), id]));
          const orderedGameIds = [
            ...prev.gameIds
              .filter((id) => fetchedByNormalized.has(normalizeGameId(id)))
              .map((id) => fetchedByNormalized.get(normalizeGameId(id)) ?? id),
            ...tournamentGameIds.filter((id) => !prevByNormalized.has(normalizeGameId(id))),
          ];
          return {
            gameIds: orderedGameIds,
            weightsByGameId: buildWeightsMap(orderedGameIds, normalizedWeights),
          };
        });
      } catch (err) {
        if (!silent) setError(t('tourEdit.loadError'));
        logToBff('tourEdit.load.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiBaseUrl, buildWeightsMap, logToBff, t, tourId, user?.token]
  );

  // Track pending optimistic overrides so fetchRounds doesn't clobber them
  const pendingRoundOverridesRef = useRef<Record<string, { name?: string; playDate?: string; expiresAt: number }>>({});

  const fetchRounds = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!tourId || !user?.token) return;
      const silent = options?.silent === true;
      if (!silent) {
        setRoundsLoading(true);
      }
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/tour/${tourId}?status=draft,active,playing,finished`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) throw new Error(`Failed to load rounds (${response.status})`);
        const data = (await response.json()) as TourRoundSummary[];
        // Merge server data but preserve any pending optimistic overrides
        const now = Date.now();
        const overrides = pendingRoundOverridesRef.current;
        // Clean up expired overrides
        for (const id of Object.keys(overrides)) {
          if (overrides[id].expiresAt < now) delete overrides[id];
        }
        // Build set of round IDs pending optimistic deletion
        const pendingDeleteRoundIds = new Set<string>();
        for (const entry of pendingRoundDeletesRef.current.values()) {
          pendingDeleteRoundIds.add(entry.roundId);
        }
        const merged = (data ?? [])
          .filter((r) => !pendingDeleteRoundIds.has(r.id))
          .map((r) => {
            const o = overrides[r.id];
            if (!o) return r;
            return {
              ...r,
              ...(o.name !== undefined ? { name: o.name } : {}),
              ...(o.playDate !== undefined ? { playDate: o.playDate } : {}),
            };
          });
        setRounds(merged);
      } catch (err) {
        if (!silent) setRounds([]);
        logToBff('tourEdit.rounds.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        if (!silent) setRoundsLoading(false);
      }
    },
    [apiBaseUrl, logToBff, tourId, user?.token]
  );

  const fetchGameDefinitions = useCallback(async () => {
    if (!user?.token) return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/game-definitions`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) throw new Error(`Failed to load game definitions (${response.status})`);
      const data = (await response.json()) as Array<Record<string, unknown>>;
      const normalized = (data ?? []).map((entry) => ({
        id: ((entry['id'] as string) ?? (entry['Id'] as string) ?? '').trim(),
        name: ((entry['name'] as string) ?? (entry['Name'] as string) ?? '').trim(),
        info: (entry['info'] as string | null) ?? (entry['Info'] as string | null) ?? null,
        description: (entry['description'] as string | null) ?? (entry['Description'] as string | null) ?? null,
        competitionType: (entry['competitionType'] as string | null) ?? (entry['CompetitionType'] as string | null) ?? null,
        calculation: (entry['calculation'] as string | null) ?? (entry['Calculation'] as string | null) ?? null,
        gameType: (entry['gameType'] as string | null) ?? (entry['GameType'] as string | null) ?? null,
        status: (entry['status'] as string | null) ?? (entry['Status'] as string | null) ?? null,
        defaultWeightPercent: (entry['defaultWeightPercent'] as number) ?? (entry['DefaultWeightPercent'] as number) ?? 100,
        image: (entry['image'] as string | null) ?? (entry['Image'] as string | null) ?? null,
      }));
      setGameDefinitions(normalized.filter((entry) => entry.id.length > 0 && entry.name.length > 0));
    } catch {
      setGameDefinitions([]);
    }
  }, [apiBaseUrl, user?.token]);

  const fetchRoundGames = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: {
          loading: (prev[roundId]?.gameIds?.length ?? 0) === 0,
          error: null,
          gameIds: prev[roundId]?.gameIds ?? [],
          weightsByGameId: prev[roundId]?.weightsByGameId ?? {},
        },
      }));
      try {
        const roundResponse = await fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!roundResponse.ok) throw new Error(`Failed to load round (${roundResponse.status})`);
        const data = (await roundResponse.json()) as Record<string, unknown>;
        const roundGameIds = Array.from(
          new Set(
            [
              ...((data.gameDefinitions as string[]) ?? []),
              ...((data.GameDefinitions as string[]) ?? []),
              ...((data.gameDefinitionIds as string[]) ?? []),
              ...((data.GameDefinitionIds as string[]) ?? []),
            ].filter(Boolean)
          )
        );
        const mergedGameIds = Array.from(new Set([...roundGameIds, ...tournamentGamesState.gameIds]));
        const weightsByGameId: Record<string, number> = {};
        const incomingWeights = [
          ...((data.gameWeights as Array<Record<string, unknown>>) ?? []),
          ...((data.GameWeights as Array<Record<string, unknown>>) ?? []),
        ];
        incomingWeights.forEach((entry) => {
          const id = (entry.gameDefinitionId as string) ?? (entry.GameDefinitionId as string);
          if (!id) return;
          const weight = (entry.weightPercent as number) ?? (entry.WeightPercent as number);
          if (typeof weight === 'number' && !Number.isNaN(weight)) weightsByGameId[id] = weight;
        });
        const normalizedWeights = buildWeightsMap(mergedGameIds, {
          ...weightsByGameId,
          ...tournamentGamesState.weightsByGameId,
        });
        const playerIds = Array.from(
          new Set([...((data.players as string[]) ?? []), ...((data.Players as string[]) ?? [])].filter(Boolean))
        );
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: { loading: false, error: null, gameIds: mergedGameIds, weightsByGameId: normalizedWeights },
        }));
        setRoundPlayerIdsByRoundId((prev) => ({ ...prev, [roundId]: playerIds }));
      } catch {
        setRoundGamesByRoundId((prev) => ({
          ...prev,
          [roundId]: { loading: false, error: t('tournaments.gamesLoadError'), gameIds: [], weightsByGameId: {} },
        }));
      }
    },
    [apiBaseUrl, buildWeightsMap, t, tournamentGamesState.gameIds, tournamentGamesState.weightsByGameId, user?.token]
  );

  const fetchRoundScores = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/scores`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as Array<{ playerId?: string; PlayerId?: string }>;
        const playerIds = new Set<string>();
        (data ?? []).forEach((entry) => {
          const id = entry.playerId ?? entry.PlayerId;
          if (id) playerIds.add(id);
        });
        setPlayerIdsWithScoresByRoundId((prev) => ({ ...prev, [roundId]: playerIds }));
      } catch {
        // Non-critical — silently ignore
      }
    },
    [apiBaseUrl, user?.token]
  );

  const fetchRoundGroups = useCallback(
    async (roundId: string) => {
      if (!user?.token) return;
      try {
        const response = await fetch(`${apiBaseUrl}/api/rounds/${roundId}/groups`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) return;
        const data = (await response.json()) as Record<string, unknown>;
        const rawGroups = (data.groups ?? data.Groups) as Array<Record<string, unknown>> | undefined;
        if (!Array.isArray(rawGroups)) return;
        const groups = rawGroups
          .map((g) => {
            const groupId = (g.groupId as string) ?? (g.GroupId as string);
            const rawTeams = (g.teams ?? g.Teams) as Array<Record<string, unknown>> | undefined;
            if (!groupId || !Array.isArray(rawTeams)) return null;
            const teams = rawTeams
              .map((t) => {
                const teamId = (t.teamId as string) ?? (t.TeamId as string);
                const playerIds = (t.playerIds ?? t.PlayerIds) as string[] | undefined;
                if (!teamId || !Array.isArray(playerIds)) return null;
                return { teamId, playerIds };
              })
              .filter(Boolean) as Array<{ teamId: string; playerIds: string[] }>;
            return { groupId, teams };
          })
          .filter(Boolean) as Array<{ groupId: string; teams: Array<{ teamId: string; playerIds: string[] }> }>;
        const playerOrder = ((data.playerOrder ?? data.PlayerOrder) as string[]) ?? [];
        setRoundGroupsByRoundId((prev) => ({
          ...prev,
          [roundId]: { groups, playerOrder },
        }));
      } catch {
        // Non-critical — silently ignore
      }
    },
    [apiBaseUrl, user?.token]
  );

  const fetchTourPlayers = useCallback(async () => {
    if (!tourId || !user?.token) return;
    setTourPlayersLoading(true);
    try {
      const [membersResp, platformResp, allPlayersResp] = await Promise.all([
        fetch(`${apiBaseUrl}/api/tours/${tourId}/members`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/players/platform`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
      ]);

      if (!membersResp.ok) throw new Error(`Failed to load tour members (${membersResp.status})`);

      let platformPayload: Array<Record<string, unknown>>;
      if (platformResp.ok) {
        platformPayload = (await platformResp.json()) as Array<Record<string, unknown>>;
      } else {
        const fallbackResp = await fetch(`${apiBaseUrl}/api/players`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!fallbackResp.ok) throw new Error(`Failed to load platform players`);
        const fallbackPayload = (await fallbackResp.json()) as
          | Array<Record<string, unknown>>
          | { players?: Array<Record<string, unknown>>; Players?: Array<Record<string, unknown>> };
        platformPayload = Array.isArray(fallbackPayload)
          ? fallbackPayload
          : (fallbackPayload.players ?? fallbackPayload.Players ?? []);
      }

      let allPlayersPayload: Array<Record<string, unknown>> = [];
      if (allPlayersResp.ok) {
        const payload = (await allPlayersResp.json()) as
          | Array<Record<string, unknown>>
          | { players?: Array<Record<string, unknown>>; Players?: Array<Record<string, unknown>> };
        allPlayersPayload = Array.isArray(payload) ? payload : (payload.players ?? payload.Players ?? []);
      }

      const members = (await membersResp.json()) as TourMemberSnapshot[];
      const platformPlayers = [...(platformPayload ?? []), ...allPlayersPayload]
        .map((player) => ({
          id: ((player.id as string) ?? (player.Id as string) ?? (player.playerId as string) ?? '').trim(),
          name: ((player.name as string) ?? (player.Name as string) ?? '').trim(),
          userId: ((player.userId as string | null) ?? (player.UserId as string | null)) ?? null,
          isFriend: false,
        }))
        .filter((player) => player.id.length > 0 && player.name.length > 0);

      const merged = new Map<string, TourPlayerSnapshot>();
      platformPlayers.forEach((player) => merged.set(player.id, player));
      (members ?? []).forEach((member) => {
        if (!member?.playerId) return;
        merged.set(member.playerId, {
          id: member.playerId,
          name: member.name,
          userId: member.userId ?? null,
          isFriend: friendPlayerIdsRef.current.has(member.playerId),
        });
      });

      const mergedPlayers = sortPlayersByFriendPriority(
        Array.from(merged.values()).map((player) => ({
          ...player,
          isFriend: friendPlayerIdsRef.current.has(player.id),
        }))
      );
      setAvailablePlayers(mergedPlayers);

      const currentPlayerId = user?.id
        ? mergedPlayers.find((player) => player.userId === user.id)?.id ?? null
        : null;

      const seenMemberIds = new Set<string>();
      const memberRows: ExistingPlayerRow[] = [];
      (members ?? []).forEach((member) => {
        if (!member.playerId || seenMemberIds.has(member.playerId) || memberRows.length >= MAX_PLAYERS) return;
        seenMemberIds.add(member.playerId);
        memberRows.push({
          kind: 'existing',
          rowId: `existing-${member.playerId}`,
          playerId: member.playerId,
          name: member.name,
          locked: currentPlayerId === member.playerId,
        });
      });

      const memberPlayerIds = new Set(memberRows.map((row) => row.playerId));
      setPlayerRows((prev) => {
        const draftRows = prev
          .filter((row): row is DraftPlayerRow => row.kind === 'draft')
          .filter((row) => !row.selectedPlayerId || !memberPlayerIds.has(row.selectedPlayerId))
          .map((row) => (row.status === 'pending' ? { ...row, status: 'idle' as const } : row));
        return [...memberRows, ...draftRows].slice(0, MAX_PLAYERS);
      });

      // Settle pending member add/remove if the data confirms it
      if (
        pendingMemberAddRequestIdRef.current &&
        pendingMemberAddPlayerIdRef.current &&
        memberPlayerIds.has(pendingMemberAddPlayerIdRef.current)
      ) {
        pendingMemberAddRequestIdRef.current = null;
        pendingMemberAddPlayerIdRef.current = null;
        pendingMemberAddDraftRowIdRef.current = null;
        pendingMemberAddPayloadRef.current = null;
        memberAddReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
        memberAddReconcileTimersRef.current = [];
        if (memberAddAckTimeoutRef.current) { clearTimeout(memberAddAckTimeoutRef.current); memberAddAckTimeoutRef.current = null; }
        setSaving(false);
      }
      if (
        pendingTourRemoveRequestIdRef.current &&
        pendingTourRemovePlayerIdRef.current &&
        !memberPlayerIds.has(pendingTourRemovePlayerIdRef.current)
      ) {
        const removedId = pendingTourRemovePlayerIdRef.current;
        pendingTourRemoveRequestIdRef.current = null;
        pendingTourRemovePlayerIdRef.current = null;
        pendingTourRemovePayloadRef.current = null;
        tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
        if (tourRemoveAckTimeoutRef.current) { clearTimeout(tourRemoveAckTimeoutRef.current); tourRemoveAckTimeoutRef.current = null; }
        setSaving(false);
        // Remove player from rounds
        if (removedId) {
          setRoundPlayerIdsByRoundId((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((roundId) => {
              next[roundId] = (next[roundId] ?? []).filter((id) => id !== removedId);
            });
            return next;
          });
        }
      }
      playersInitializedRef.current = true;
    } catch {
      setAvailablePlayers([]);
      if (!playersInitializedRef.current) {
        setPlayerRows((prev) => prev.filter((row) => row.kind === 'draft'));
        playersInitializedRef.current = true;
      }
    } finally {
      setTourPlayersLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, sortPlayersByFriendPriority, tourId, user?.id, user?.token]);

  const fetchCourses = useCallback(
    async (coords: { lat: number; lon: number }) => {
      if (!user?.token) return;
      setLoadingCourses(true);
      setCourseError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses?lat=${coords.lat}&lon=${coords.lon}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (!response.ok) throw new Error(`Failed to load courses (${response.status})`);
        const data = (await response.json()) as Course[];
        setCourses(data ?? []);
      } catch {
        setCourses([]);
        setCourseError(t('course.error'));
      } finally {
        setLoadingCourses(false);
      }
    },
    [apiBaseUrl, t, user?.token]
  );

  const coursesPrefetchedRef = useRef(false);
  useEffect(() => {
    if (coursesPrefetchedRef.current) return;
    if (!user?.token) return;
    if (courses.length > 0 || loadingCourses) return;
    coursesPrefetchedRef.current = true;
    fetchCourses({ lat: 57.7089, lon: 11.9746 });
  }, [user?.token, courses.length, loadingCourses, fetchCourses]);

  // --- Settle/reconcile helpers ---
  const settlePendingTourMemberAdd = useCallback(
    (statusKey: string = 'tourEdit.saveAck') => {
      pendingMemberAddRequestIdRef.current = null;
      pendingMemberAddPlayerIdRef.current = null;
      pendingMemberAddDraftRowIdRef.current = null;
      pendingMemberAddPayloadRef.current = null;
      memberAddReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
      memberAddReconcileTimersRef.current = [];
      if (memberAddAckTimeoutRef.current) {
        clearTimeout(memberAddAckTimeoutRef.current);
        memberAddAckTimeoutRef.current = null;
      }
      setSaving(false);
      setStatusNote(t(statusKey));
    },
    [t]
  );

  const settlePendingTourMemberRemove = useCallback(
    (statusKey: string = 'tourEdit.memberRemoved') => {
      pendingTourRemoveRequestIdRef.current = null;
      pendingTourRemovePlayerIdRef.current = null;
      pendingTourRemovePayloadRef.current = null;
      tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
      tourRemoveReconcileTimersRef.current = [];
      if (tourRemoveAckTimeoutRef.current) {
        clearTimeout(tourRemoveAckTimeoutRef.current);
        tourRemoveAckTimeoutRef.current = null;
      }
      setSaving(false);
      setStatusNote(t(statusKey));
    },
    [t]
  );

  const applyTourMemberRemovalToRoundState = useCallback(
    (playerId: string, affectedRoundIds?: string[]) => {
      if (!playerId) return;
      const targetIds = new Set<string>();
      (affectedRoundIds ?? []).map((id) => id.trim()).filter((id) => id.length > 0).forEach((id) => targetIds.add(id));
      if (targetIds.size === 0) {
        rounds.forEach((round) => { if (round.id) targetIds.add(round.id); });
      }
      const ids = Array.from(targetIds);
      if (ids.length === 0) return;
      setRoundPlayerIdsByRoundId((prev) => {
        const next = { ...prev };
        ids.forEach((roundId) => { next[roundId] = (next[roundId] ?? []).filter((id) => id !== playerId); });
        return next;
      });
    },
    [rounds]
  );

  const scheduleTourRoundReconcile = useCallback(() => {
    reconcileRefreshTimersRef.current.forEach((timer) => clearTimeout(timer));
    reconcileRefreshTimersRef.current = [];
    [900, 2600, 5000].forEach((delayMs) => {
      const timer = setTimeout(() => {
        fetchTour({ silent: true, preserveName: true });
        fetchRounds({ silent: true });
      }, delayMs);
      reconcileRefreshTimersRef.current.push(timer);
    });
  }, [fetchRounds, fetchTour]);

  const scheduleTourMemberAddReconcile = useCallback(() => {
    memberAddReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
    memberAddReconcileTimersRef.current = [];
    [350, 1200, 2800].forEach((delayMs) => {
      const timer = setTimeout(() => { void fetchTourPlayers(); }, delayMs);
      memberAddReconcileTimersRef.current.push(timer);
    });
  }, [fetchTourPlayers]);

  const scheduleTourMemberRemoveReconcile = useCallback(() => {
    tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
    tourRemoveReconcileTimersRef.current = [];
    [350, 1200, 2800].forEach((delayMs) => {
      const timer = setTimeout(() => {
        void fetchTourPlayers();
        void fetchRounds({ silent: true });
      }, delayMs);
      tourRemoveReconcileTimersRef.current.push(timer);
    });
  }, [fetchRounds, fetchTourPlayers]);

  const removeRoundFromLocalState = useCallback((roundId: string) => {
    if (!roundId) return;
    setRounds((prev) => prev.filter((round) => round.id !== roundId));
    setRoundGamesByRoundId((prev) => { const next = { ...prev }; delete next[roundId]; return next; });
    setRoundPlayerIdsByRoundId((prev) => { const next = { ...prev }; delete next[roundId]; return next; });
  }, []);

  // --- Send functions ---
  const sendTourUpdate = useCallback(
    (reason: string, options?: { tournamentGameIds?: string[]; tournamentWeightsByGameId?: Record<string, number> }) => {
      if (!tourId || !user?.token) return;
      const trimmed = tourName.trim();
      if (!trimmed) {
        setError(t('tourEdit.nameRequired'));
        return;
      }
      const tgIds = Array.from(new Set((options?.tournamentGameIds ?? tournamentGamesState.gameIds).filter(Boolean)));
      const tgWeights = buildWeightsMap(tgIds, options?.tournamentWeightsByGameId ?? tournamentGamesState.weightsByGameId);
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.saving'));
      const requestId = generateId();
      pendingRequestIdRef.current = requestId;
      const payload = {
        type: 'tour.update',
        tourId,
        requestId,
        tourName: trimmed,
        tournamentGameDefinitionIds: tgIds,
        tournamentGameWeights: tgIds.map((gameDefinitionId) => ({
          gameDefinitionId,
          weightPercent: tgWeights[gameDefinitionId] ?? getDefaultWeightForGame(gameDefinitionId),
        })),
      };
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingPayloadRef.current = payload;
        setStatusNote(t('tourEdit.saveQueued'));
      } else {
        setStatusNote(t('tourEdit.saveSent'));
      }
      if (ackTimeoutRef.current) clearTimeout(ackTimeoutRef.current);
      ackTimeoutRef.current = setTimeout(() => {
        if (pendingRequestIdRef.current !== requestId) return;
        pendingRequestIdRef.current = null;
        pendingTournamentSyncRef.current = null;
        setSaving(false);
        setError(t('tourEdit.saveFailed'));
        logToBff('tourEdit.update.timeout', { tourId, requestId, reason }, 'warn');
      }, ACK_TIMEOUT_MS);
    },
    [buildWeightsMap, getDefaultWeightForGame, logToBff, t, tourId, tourName, tournamentGamesState.gameIds, tournamentGamesState.weightsByGameId, user?.token]
  );

  const updateTournamentGames = useCallback(
    (gameIds: string[], reason: string = 'tour.tournament.games') => {
      const seenIds = new Set<string>();
      const uniqueGameIds = gameIds.filter((id) => {
        const n = normalizeGameId(id);
        if (!n || seenIds.has(n)) return false;
        seenIds.add(n);
        return true;
      });
      const normalizedWeights = buildWeightsMap(uniqueGameIds, tournamentGamesState.weightsByGameId ?? {});
      pendingTournamentSyncRef.current = { gameIds: uniqueGameIds, weightsByGameId: normalizedWeights };
      setTournamentGamesState({ gameIds: uniqueGameIds, weightsByGameId: normalizedWeights });

      // Update round games to reflect tournament game changes
      const nextTournamentSet = new Set(uniqueGameIds.map(normalizeGameId));
      const removedIds = new Set(
        tournamentGamesState.gameIds.map(normalizeGameId).filter((id) => !nextTournamentSet.has(id))
      );
      setRoundGamesByRoundId((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([roundId, state]) => {
          const retainedGameIds = (state.gameIds ?? []).filter((id) => !removedIds.has(normalizeGameId(id)));
          const nextGameIds = Array.from(new Set([...retainedGameIds, ...uniqueGameIds]));
          const mergedWeights = buildWeightsMap(nextGameIds, { ...(state.weightsByGameId ?? {}), ...normalizedWeights });
          next[roundId] = { ...state, loading: false, error: null, gameIds: nextGameIds, weightsByGameId: mergedWeights };
        });
        return next;
      });

      sendTourUpdate(reason, { tournamentGameIds: uniqueGameIds, tournamentWeightsByGameId: normalizedWeights });
    },
    [buildWeightsMap, sendTourUpdate, tournamentGamesState.gameIds, tournamentGamesState.weightsByGameId]
  );

  const updateTournamentGameWeight = useCallback(
    (gameId: string, weightPercent: number) => {
      const gameIds = tournamentGamesState.gameIds;
      if (!gameIds.some((id) => normalizeGameId(id) === normalizeGameId(gameId))) return;
      const normalizedWeights = buildWeightsMap(gameIds, {
        ...(tournamentGamesState.weightsByGameId ?? {}),
        [gameId]: weightPercent,
      });
      pendingTournamentSyncRef.current = { gameIds, weightsByGameId: normalizedWeights };
      setTournamentGamesState({ gameIds, weightsByGameId: normalizedWeights });

      // Tour game weights apply to every round that includes the game.
      setRoundGamesByRoundId((prev) => {
        const next: typeof prev = {};
        Object.entries(prev).forEach(([roundId, state]) => {
          const mergedWeights = buildWeightsMap(state.gameIds ?? [], {
            ...(state.weightsByGameId ?? {}),
            [gameId]: weightPercent,
          });
          next[roundId] = { ...state, weightsByGameId: mergedWeights };
        });
        return next;
      });

      sendTourUpdate('tour.tournament.gameWeight', {
        tournamentGameIds: gameIds,
        tournamentWeightsByGameId: normalizedWeights,
      });
    },
    [buildWeightsMap, sendTourUpdate, tournamentGamesState.gameIds, tournamentGamesState.weightsByGameId]
  );

  const sendTourMemberAdd = useCallback(
    (playerId: string, reason: string, draftRowId?: string) => {
      if (!tourId || !user?.token || !playerId) return;
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.saving'));
      const requestId = generateId();
      pendingMemberAddRequestIdRef.current = requestId;
      pendingMemberAddPlayerIdRef.current = playerId;
      pendingMemberAddDraftRowIdRef.current = draftRowId ?? null;
      const payload = { type: 'tour.member.add', tourId, requestId, playerId };
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingMemberAddPayloadRef.current = payload;
        setStatusNote(t('tourEdit.saveQueued'));
      } else {
        setStatusNote(t('tourEdit.saveSent'));
      }
      if (memberAddAckTimeoutRef.current) clearTimeout(memberAddAckTimeoutRef.current);
      memberAddAckTimeoutRef.current = setTimeout(() => {
        if (pendingMemberAddRequestIdRef.current !== requestId) return;
        pendingMemberAddRequestIdRef.current = null;
        pendingMemberAddPlayerIdRef.current = null;
        pendingMemberAddDraftRowIdRef.current = null;
        pendingMemberAddPayloadRef.current = null;
        memberAddReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
        memberAddReconcileTimersRef.current = [];
        setSaving(false);
        setError(t('tourEdit.saveFailed'));
        if (draftRowId) {
          setPlayerRows((prev) =>
            prev.map((row) =>
              row.kind === 'draft' && row.rowId === draftRowId ? { ...row, status: 'idle' } : row
            )
          );
        }
      }, ACK_TIMEOUT_MS);
      scheduleTourMemberAddReconcile();
    },
    [scheduleTourMemberAddReconcile, t, tourId, user?.token]
  );

  const sendTourMemberRemove = useCallback(
    (playerId: string) => {
      if (!tourId || !user?.token || !playerId) return;
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.memberRemoving'));
      const requestId = generateId();
      pendingTourRemoveRequestIdRef.current = requestId;
      pendingTourRemovePlayerIdRef.current = playerId;
      const payload = buildTourMemberRemoveMessage(tourId, requestId, playerId);
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingTourRemovePayloadRef.current = payload;
        setStatusNote(t('tourEdit.saveQueued'));
      } else {
        setStatusNote(t('tourEdit.memberRemoveSent'));
      }
      if (tourRemoveAckTimeoutRef.current) clearTimeout(tourRemoveAckTimeoutRef.current);
      tourRemoveAckTimeoutRef.current = setTimeout(() => {
        if (pendingTourRemoveRequestIdRef.current !== requestId) return;
        pendingTourRemoveRequestIdRef.current = null;
        pendingTourRemovePlayerIdRef.current = null;
        pendingTourRemovePayloadRef.current = null;
        tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
        tourRemoveReconcileTimersRef.current = [];
        setSaving(false);
        setError(t('tourEdit.memberRemoveTimeout'));
      }, ACK_TIMEOUT_MS);
      scheduleTourMemberRemoveReconcile();
    },
    [scheduleTourMemberRemoveReconcile, t, tourId, user?.token]
  );

  const sendRoundMemberAdd = useCallback(
    (roundId: string, playerId: string) => {
      if (!roundId || !playerId || !tourId || !user?.token) return;
      // Clear any previous pending add so we don't block this one
      if (pendingRoundAddRequestIdRef.current) {
        if (roundAddAckTimeoutRef.current) { clearTimeout(roundAddAckTimeoutRef.current); roundAddAckTimeoutRef.current = null; }
        pendingRoundAddRequestIdRef.current = null;
      }
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.saving'));
      const requestId = generateId();
      pendingRoundAddRequestIdRef.current = requestId;
      pendingRoundAddRoundIdRef.current = roundId;
      pendingRoundMemberChangesRef.current[requestId] = { action: 'add', roundId, playerId, hadRecordedScores: false };
      setRoundPlayerIdsByRoundId((prev) => {
        const existing = new Set(prev[roundId] ?? []);
        existing.add(playerId);
        return { ...prev, [roundId]: Array.from(existing) };
      });
      const payload = buildRoundMemberAddMessage(roundId, requestId, playerId);
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingRoundAddPayloadRef.current = payload;
        setStatusNote(t('tourEdit.saveQueued'));
      }
      if (roundAddAckTimeoutRef.current) clearTimeout(roundAddAckTimeoutRef.current);
      roundAddAckTimeoutRef.current = setTimeout(() => {
        if (pendingRoundAddRequestIdRef.current !== requestId) return;
        pendingRoundAddRequestIdRef.current = null;
        pendingRoundAddRoundIdRef.current = null;
        delete pendingRoundMemberChangesRef.current[requestId];
        // Don't revert — the operation is likely persisted. Ack should arrive via WebSocket subscription.
        setSaving(false);
        setStatusNote('');
      }, ACK_TIMEOUT_MS);
    },
    [t, tourId, user?.token]
  );

  const sendRoundMemberRemove = useCallback(
    (roundId: string, playerId: string) => {
      if (!roundId || !playerId || !tourId || !user?.token) return;
      setError(null);
      setSaving(true);
      const requestId = generateId();
      pendingRoundRemoveRequestIdRef.current = requestId;
      pendingRoundMemberChangesRef.current[requestId] = { action: 'remove', roundId, playerId, hadRecordedScores: false };
      setRoundPlayerIdsByRoundId((prev) => ({
        ...prev,
        [roundId]: (prev[roundId] ?? []).filter((id) => id !== playerId),
      }));
      const payload = buildRoundMemberRemoveMessage(roundId, requestId, playerId);
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingRoundRemovePayloadRef.current = payload;
      }
      if (roundRemoveAckTimeoutRef.current) clearTimeout(roundRemoveAckTimeoutRef.current);
      roundRemoveAckTimeoutRef.current = setTimeout(() => {
        if (pendingRoundRemoveRequestIdRef.current !== requestId) return;
        pendingRoundRemoveRequestIdRef.current = null;
        delete pendingRoundMemberChangesRef.current[requestId];
        // Don't revert — the operation is likely persisted. Ack should arrive via WebSocket subscription.
        setSaving(false);
        setStatusNote('');
      }, ACK_TIMEOUT_MS);
    },
    [t, tourId, user?.token]
  );

  const sendRoundGroupsSwap = useCallback(
    (roundId: string, playerIdA: string, playerIdB: string) => {
      if (!roundId || !playerIdA || !playerIdB || !tourId || !user?.token) return;
      setError(null);
      const requestId = generateId();
      pendingSwapRequestIdRef.current = requestId;

      // Optimistic swap: exchange the two players' group+team positions
      setRoundGroupsByRoundId((prev) => {
        const data = prev[roundId];
        if (!data) return prev;
        const groups = data.groups.map((group) => ({
          ...group,
          teams: group.teams.map((team) => ({
            ...team,
            playerIds: team.playerIds.map((id) =>
              id === playerIdA ? playerIdB : id === playerIdB ? playerIdA : id
            ),
          })),
        }));
        const playerOrder = data.playerOrder.map((id) =>
          id === playerIdA ? playerIdB : id === playerIdB ? playerIdA : id
        );
        return { ...prev, [roundId]: { groups, playerOrder } };
      });

      const payload = buildRoundGroupsSwapMessage(roundId, requestId, playerIdA, playerIdB);
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingSwapPayloadRef.current = payload;
      }
      if (swapAckTimeoutRef.current) clearTimeout(swapAckTimeoutRef.current);
      swapAckTimeoutRef.current = setTimeout(() => {
        if (pendingSwapRequestIdRef.current !== requestId) return;
        pendingSwapRequestIdRef.current = null;
        setStatusNote('');
      }, ACK_TIMEOUT_MS);
    },
    [tourId, user?.token]
  );

  const sendRoundRemove = useCallback(
    (roundId: string) => {
      if (!roundId || !tourId || !user?.token) return;
      // Optimistic rounds haven't been persisted yet — just remove locally
      if (roundId.startsWith('optimistic-')) {
        removeRoundFromLocalState(roundId);
        return;
      }
      setError(null);
      setSaving(true);
      setStatusNote(t('tourEdit.roundRemoving'));
      const requestId = generateId();
      const payload = buildRoundRemoveMessage(roundId, requestId);
      const sent = socketRef.current.send(payload);
      if (!sent) {
        pendingRoundDeletePayloadRef.current = payload;
      }
      // Optimistically remove from local state immediately
      removeRoundFromLocalState(roundId);
      const timeout = setTimeout(() => {
        pendingRoundDeletesRef.current.delete(requestId);
        if (pendingRoundDeletesRef.current.size === 0) setSaving(false);
        // Re-fetch to sync in case of missed acks
        fetchRounds({ silent: true });
      }, ACK_TIMEOUT_MS);
      pendingRoundDeletesRef.current.set(requestId, { roundId, timeout });
    },
    [fetchRounds, removeRoundFromLocalState, t, tourId, user?.token]
  );

  const sendCreateRound = useCallback(
    (course: Course, tourPlayers: Array<{ id: string }>, playDate: string, roundName?: string, holesCount: number = 18): string | undefined => {
      if (!tourId || !user?.token) return undefined;
      if (tourPlayers.length === 0) {
        setError(t('tourEdit.roundCreateNoPlayers'));
        return undefined;
      }
      const requestId = generateId();
      const optimisticId = `optimistic-${requestId}`;
      const name = roundName || '';
      const payload = {
        type: 'round.initiate',
        requestId,
        tourId,
        name,
        holesCount,
        playDate,
        course,
        players: tourPlayers,
        gameDefinitionIds: [],
      };
      const socket = socketRef.current;
      socket.setAuthToken(user?.token ?? null);
      const sent = socket.send(payload);
      if (!sent) {
        setError(t('tourEdit.roundCreateFailed'));
        return undefined;
      }

      // Optimistically add round to local state
      setRounds((prev) => [
        ...prev,
        { id: optimisticId, name, playDate, courseName: course.name, holesCount, status: 'draft' },
      ]);

      setSaving(true);
      setStatusNote(t('tourEdit.saving'));
      // Fetch rounds to replace optimistic entry with real one
      setTimeout(() => {
        fetchRounds({ silent: true });
      }, 1500);
      setTimeout(() => {
        fetchRounds({ silent: true });
      }, 4000);

      return optimisticId;
    },
    [fetchRounds, t, tourId, user?.token]
  );

  const sendTemporaryPlayerCreate = useCallback(
    (name: string) => {
      if (!user?.token || !name.trim()) return;
      const requestId = generateId();
      socketRef.current.setAuthToken(user.token);
      socketRef.current.send({
        type: 'player.temporary.create',
        requestId,
        name: name.trim(),
      });
      return requestId;
    },
    [user?.token]
  );

  const updateRoundGames = useCallback(
    (roundId: string, gameIds: string[]) => {
      if (!roundId || !user?.token) return;
      const unique = Array.from(new Set([...gameIds.filter(Boolean), ...tournamentGamesState.gameIds]));
      const weightsByGameId = buildWeightsMap(unique, {
        ...(roundGamesByRoundId[roundId]?.weightsByGameId ?? {}),
        ...tournamentGamesState.weightsByGameId,
      });
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: { loading: false, error: null, gameIds: unique, weightsByGameId },
      }));
      const socket = socketRef.current;
      socket.setAuthToken(user?.token ?? null);
      const payload = buildRoundUpdateMessage({
        roundId,
        gameDefinitionIds: unique,
        gameWeights: unique.map((id) => ({
          gameDefinitionId: id,
          weightPercent: tournamentGameIdSet.has(id)
            ? (tournamentGamesState.weightsByGameId[id] ?? getDefaultWeightForGame(id))
            : (weightsByGameId[id] ?? getDefaultWeightForGame(id)),
        })),
      });
      socket.send(payload);
    },
    [buildWeightsMap, getDefaultWeightForGame, roundGamesByRoundId, tournamentGameIdSet, tournamentGamesState.gameIds, tournamentGamesState.weightsByGameId, user?.token]
  );

  const updateRoundGameWeight = useCallback(
    (roundId: string, gameId: string, weightPercent: number) => {
      if (!roundId || !user?.token) return;
      // Tour-managed games get their weight from the tour.
      if (tournamentGameIdSet.has(normalizeGameId(gameId))) return;
      const state = roundGamesByRoundId[roundId];
      const gameIds = state?.gameIds ?? [];
      if (!gameIds.some((id) => normalizeGameId(id) === normalizeGameId(gameId))) return;
      const weightsByGameId = buildWeightsMap(gameIds, {
        ...(state?.weightsByGameId ?? {}),
        [gameId]: weightPercent,
      });
      setRoundGamesByRoundId((prev) => ({
        ...prev,
        [roundId]: { ...(prev[roundId] ?? { loading: false, error: null }), gameIds, weightsByGameId },
      }));
      const socket = socketRef.current;
      socket.setAuthToken(user?.token ?? null);
      const payload = buildRoundUpdateMessage({
        roundId,
        gameDefinitionIds: gameIds,
        gameWeights: gameIds.map((id) => ({
          gameDefinitionId: id,
          weightPercent: tournamentGameIdSet.has(normalizeGameId(id))
            ? (tournamentGamesState.weightsByGameId[id] ?? getDefaultWeightForGame(id))
            : (weightsByGameId[id] ?? getDefaultWeightForGame(id)),
        })),
      });
      socket.send(payload);
    },
    [buildWeightsMap, getDefaultWeightForGame, roundGamesByRoundId, tournamentGameIdSet, tournamentGamesState.weightsByGameId, user?.token]
  );

  const updateRoundSettings = useCallback(
    (roundId: string, updates: { name?: string; playDate?: string; course?: Course; holesCount?: number }) => {
      if (!roundId || !user?.token) return;
      // Skip sending to backend for optimistic (not yet persisted) rounds
      if (roundId.startsWith('optimistic-')) return;

      // Record pending overrides (protect from fetchRounds for 3 seconds)
      const existing = pendingRoundOverridesRef.current[roundId];
      pendingRoundOverridesRef.current[roundId] = {
        ...(existing ?? {}),
        ...(updates.name !== undefined ? { name: updates.name } : {}),
        ...(updates.playDate !== undefined ? { playDate: updates.playDate } : {}),
        expiresAt: Date.now() + 3000,
      };

      // Optimistically update local rounds state so sidebar reflects changes immediately
      // Use functional updater to read current state (avoids stale closure)
      setRounds((prev) => {
        const currentRound = prev.find((r) => r.id === roundId);
        const resolvedPlayDate = updates.playDate !== undefined ? updates.playDate : (currentRound?.playDate ?? undefined);
        const resolvedName = updates.name !== undefined ? updates.name : (currentRound?.name ?? undefined);

        // Send update via WebSocket — only include changed fields
        const socket = socketRef.current;
        socket.setAuthToken(user?.token ?? null);
        const payload: Record<string, unknown> = {
          type: 'round.update',
          roundId,
        };
        if (resolvedPlayDate) payload.playDate = resolvedPlayDate;
        if (resolvedName) payload.name = resolvedName;
        if (updates.course) payload.course = { id: updates.course.id, name: updates.course.name, address: updates.course.address, lat: updates.course.lat, lon: updates.course.lon };
        if (updates.holesCount === 9 || updates.holesCount === 18) payload.holesCount = updates.holesCount;
        socket.send(payload);

        return prev.map((r) =>
          r.id === roundId
            ? {
                ...r,
                ...(updates.name !== undefined ? { name: updates.name } : {}),
                ...(updates.playDate !== undefined ? { playDate: updates.playDate } : {}),
                ...(updates.course ? { courseName: updates.course.name } : {}),
                ...(updates.holesCount !== undefined ? { holesCount: updates.holesCount } : {}),
              }
            : r
        );
      });
    },
    [user?.token]
  );

  // --- WebSocket event handler (use refs to avoid re-subscribing on every state change) ---
  const handlersRef = useRef({
    fetchTourPlayers,
    fetchRounds,
    fetchRoundGames,
    buildWeightsMap,
    tournamentGamesState,
    scheduleTourRoundReconcile,
    scheduleTourMemberAddReconcile,
    scheduleTourMemberRemoveReconcile,
    removeRoundFromLocalState,
    sendTourMemberAdd,
  });
  useEffect(() => {
    handlersRef.current = {
      fetchTourPlayers,
      fetchRounds,
      fetchRoundGames,
      buildWeightsMap,
      tournamentGamesState,
      scheduleTourRoundReconcile,
      scheduleTourMemberAddReconcile,
      scheduleTourMemberRemoveReconcile,
      removeRoundFromLocalState,
      sendTourMemberAdd,
    };
  });

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    socket.setSubscriptionContext({ tourId });
    const unsubscribe = socket.onMessage((message) => {
      const h = handlersRef.current;
      const type = (message as { type?: string }).type;

      if (type === 'tour.update.ack') {
        const payload = message as { requestId?: string; tourId?: string };
        if (pendingRequestIdRef.current && payload.requestId && payload.requestId !== pendingRequestIdRef.current) {
          h.fetchTourPlayers();
          h.scheduleTourRoundReconcile();
          return;
        }
        pendingRequestIdRef.current = null;
        if (ackTimeoutRef.current) { clearTimeout(ackTimeoutRef.current); ackTimeoutRef.current = null; }
        setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        h.scheduleTourRoundReconcile();
        return;
      }

      if (type === 'tour.member.added') {
        const payload = message as { requestId?: string; member?: { playerId?: string } };
        if (pendingMemberAddRequestIdRef.current && payload.requestId && payload.requestId !== pendingMemberAddRequestIdRef.current) {
          if (pendingMemberAddPlayerIdRef.current && payload.member?.playerId === pendingMemberAddPlayerIdRef.current) {
            settlePendingTourMemberAdd();
          }
          h.fetchTourPlayers();
          h.scheduleTourMemberAddReconcile();
          return;
        }
        if (pendingMemberAddRequestIdRef.current) settlePendingTourMemberAdd();
        h.fetchTourPlayers();
        return;
      }

      if (type === 'friend.list.snapshot') {
        const payload = message as { friends?: Array<{ playerId?: string; playerName?: string; name?: string; userId?: string | null }> };
        const friendIds = new Set(
          (payload.friends ?? []).map((f) => f.playerId).filter((id): id is string => Boolean(id))
        );
        friendPlayerIdsRef.current = friendIds;
        setAvailablePlayers((prev) => {
          const merged = new Map<string, TourPlayerSnapshot>(
            prev.map((p) => [p.id, { ...p, isFriend: friendIds.has(p.id) }])
          );
          (payload.friends ?? []).forEach((friend) => {
            if (!friend.playerId) return;
            const existing = merged.get(friend.playerId);
            merged.set(friend.playerId, {
              id: friend.playerId,
              name: (friend.playerName ?? friend.name ?? '').trim() || existing?.name || friend.playerId,
              userId: friend.userId ?? existing?.userId ?? null,
              isFriend: true,
            });
          });
          return sortPlayersByFriendPriority(Array.from(merged.values()));
        });
        return;
      }

      if (type === 'tour.member.removed') {
        const payload = message as { requestId?: string; playerId?: string; removedFromRoundIds?: string[] };
        if (pendingTourRemoveRequestIdRef.current && payload.requestId && payload.requestId !== pendingTourRemoveRequestIdRef.current) {
          if (pendingTourRemovePlayerIdRef.current && payload.playerId === pendingTourRemovePlayerIdRef.current) {
            settlePendingTourMemberRemove();
          }
          if (payload.playerId) {
            setRoundPlayerIdsByRoundId((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((roundId) => {
                next[roundId] = (next[roundId] ?? []).filter((id) => id !== payload.playerId);
              });
              return next;
            });
          }
          h.fetchTourPlayers();
          h.fetchRounds();
          h.scheduleTourMemberRemoveReconcile();
          return;
        }
        settlePendingTourMemberRemove();
        if (payload.playerId) {
          setRoundPlayerIdsByRoundId((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((roundId) => {
              next[roundId] = (next[roundId] ?? []).filter((id) => id !== payload.playerId);
            });
            return next;
          });
        }
        h.fetchTourPlayers();
        h.fetchRounds();
        return;
      }

      if (type === 'tour.member.remove.rejected') {
        const payload = message as { requestId?: string; messageKey?: string; code?: string; message?: string };
        if (pendingTourRemoveRequestIdRef.current && payload.requestId && payload.requestId !== pendingTourRemoveRequestIdRef.current) return;
        pendingTourRemoveRequestIdRef.current = null;
        pendingTourRemovePlayerIdRef.current = null;
        pendingTourRemovePayloadRef.current = null;
        tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
        if (tourRemoveAckTimeoutRef.current) { clearTimeout(tourRemoveAckTimeoutRef.current); tourRemoveAckTimeoutRef.current = null; }
        setSaving(false);
        setError(resolveMemberRemoveRejectedMessage(t, 'tourEdit.memberRemoveRejected', payload.messageKey, payload.code, payload.message));
        return;
      }

      if (type === 'round.member.added') {
        const payload = message as { requestId?: string; roundId?: string; playerId?: string };
        if (payload.requestId) delete pendingRoundMemberChangesRef.current[payload.requestId];
        if (pendingRoundAddRequestIdRef.current && payload.requestId && payload.requestId !== pendingRoundAddRequestIdRef.current) {
          if (payload.roundId && payload.playerId) {
            setRoundPlayerIdsByRoundId((prev) => {
              const existing = new Set(prev[payload.roundId!] ?? []);
              existing.add(payload.playerId!);
              return { ...prev, [payload.roundId!]: Array.from(existing) };
            });
          }
          return;
        }
        pendingRoundAddRequestIdRef.current = null;
        pendingRoundAddRoundIdRef.current = null;
        if (roundAddAckTimeoutRef.current) { clearTimeout(roundAddAckTimeoutRef.current); roundAddAckTimeoutRef.current = null; }
        setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        if (payload.roundId && payload.playerId) {
          setRoundPlayerIdsByRoundId((prev) => {
            const existing = new Set(prev[payload.roundId!] ?? []);
            existing.add(payload.playerId!);
            return { ...prev, [payload.roundId!]: Array.from(existing) };
          });
        }
        return;
      }

      if (type === 'round.member.removed') {
        const payload = message as { requestId?: string; roundId?: string; playerId?: string };
        if (payload.requestId) delete pendingRoundMemberChangesRef.current[payload.requestId];
        pendingRoundRemoveRequestIdRef.current = null;
        if (roundRemoveAckTimeoutRef.current) { clearTimeout(roundRemoveAckTimeoutRef.current); roundRemoveAckTimeoutRef.current = null; }
        setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        if (payload.roundId && payload.playerId) {
          setRoundPlayerIdsByRoundId((prev) => ({
            ...prev,
            [payload.roundId!]: (prev[payload.roundId!] ?? []).filter((id) => id !== payload.playerId),
          }));
        }
        return;
      }

      if (type === 'round.member.remove.rejected' || type === 'round.member.add.rejected') {
        const payload = message as { requestId?: string; roundId?: string; playerId?: string };
        const pending = payload.requestId ? pendingRoundMemberChangesRef.current[payload.requestId] : undefined;
        if (pending) {
          if (pending.action === 'add') {
            setRoundPlayerIdsByRoundId((prev) => ({
              ...prev,
              [pending.roundId]: (prev[pending.roundId] ?? []).filter((id) => id !== pending.playerId),
            }));
          } else {
            setRoundPlayerIdsByRoundId((prev) => {
              const existing = new Set(prev[pending.roundId] ?? []);
              existing.add(pending.playerId);
              return { ...prev, [pending.roundId]: Array.from(existing) };
            });
          }
          delete pendingRoundMemberChangesRef.current[payload.requestId!];
        }
        pendingRoundAddRequestIdRef.current = null;
        pendingRoundRemoveRequestIdRef.current = null;
        if (roundAddAckTimeoutRef.current) { clearTimeout(roundAddAckTimeoutRef.current); roundAddAckTimeoutRef.current = null; }
        if (roundRemoveAckTimeoutRef.current) { clearTimeout(roundRemoveAckTimeoutRef.current); roundRemoveAckTimeoutRef.current = null; }
        setSaving(false);
        setError(t('tourEdit.saveFailed'));
        return;
      }

      if (type === 'round.created') {
        const payload = message as { requestId?: string; roundId?: string; tourId?: string; playDate?: string };
        setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        h.fetchRounds({ silent: true });
        if (payload.roundId) {
          fetchedRoundIdsRef.current.delete(payload.roundId);
          h.fetchRoundGames(payload.roundId);
        }
        return;
      }

      if (type === 'round.removed') {
        const payload = message as { requestId?: string; roundId?: string };
        if (payload.requestId && pendingRoundDeletesRef.current.has(payload.requestId)) {
          const entry = pendingRoundDeletesRef.current.get(payload.requestId)!;
          clearTimeout(entry.timeout);
          pendingRoundDeletesRef.current.delete(payload.requestId);
        }
        if (pendingRoundDeletesRef.current.size === 0) setSaving(false);
        setStatusNote(t('tourEdit.saveAck'));
        if (payload.roundId) h.removeRoundFromLocalState(payload.roundId);
        return;
      }

      if (type === 'round.remove.rejected') {
        const payload = message as { requestId?: string; message?: string };
        if (payload.requestId && pendingRoundDeletesRef.current.has(payload.requestId)) {
          const entry = pendingRoundDeletesRef.current.get(payload.requestId)!;
          clearTimeout(entry.timeout);
          pendingRoundDeletesRef.current.delete(payload.requestId);
        }
        if (pendingRoundDeletesRef.current.size === 0) setSaving(false);
        // Re-fetch rounds to sync with backend — the round may already be gone
        h.fetchRounds({ silent: true });
        return;
      }

      if (type === 'round.updated') {
        const payload = message as {
          roundId?: string;
          gameDefinitionIds?: string[];
          gameWeights?: Array<{ gameDefinitionId?: string; weightPercent?: number }>;
          course?: { id?: string; name?: string; address?: string; lat?: number; lon?: number };
          holesCount?: number;
          playDate?: string;
          name?: string;
        };
        if (!payload.roundId) return;
        // Update round metadata (name, playDate, course, holesCount)
        if (payload.playDate || payload.name != null || payload.course || payload.holesCount) {
          setRounds((prev) =>
            prev.map((round) =>
              round.id === payload.roundId
                ? {
                    ...round,
                    ...(payload.playDate ? { playDate: payload.playDate } : {}),
                    ...(payload.name != null ? { name: payload.name } : {}),
                    ...(payload.course?.name ? { courseName: payload.course.name } : {}),
                    ...(payload.holesCount ? { holesCount: payload.holesCount } : {}),
                  }
                : round
            )
          );
        }
        // Update round game definitions and weights
        if (payload.gameDefinitionIds) {
          const tgs = h.tournamentGamesState;
          setRoundGamesByRoundId((prev) => {
            const existing = prev[payload.roundId!];
            const gameIds = Array.from(new Set([
              ...(payload.gameDefinitionIds ?? []),
              ...tgs.gameIds,
            ]));
            const mergedWeights: Record<string, number> = {
              ...(existing?.weightsByGameId ?? {}),
              ...tgs.weightsByGameId,
            };
            (payload.gameWeights ?? []).forEach((entry) => {
              if (entry.gameDefinitionId && typeof entry.weightPercent === 'number' && !Number.isNaN(entry.weightPercent)) {
                mergedWeights[entry.gameDefinitionId] = entry.weightPercent;
              }
            });
            return {
              ...prev,
              [payload.roundId!]: {
                loading: false,
                error: null,
                gameIds,
                weightsByGameId: h.buildWeightsMap(gameIds, mergedWeights),
              },
            };
          });
        }
        return;
      }

      if (type === 'round.update.rejected') {
        const payload = message as { roundId?: string; code?: string; messageKey?: string; message?: string };
        setSaving(false);
        setError(payload.message ?? t('tourEdit.saveFailed'));
        // Revert optimistic changes by re-fetching
        if (payload.roundId) {
          h.fetchRounds({ silent: true });
          h.fetchRoundGames(payload.roundId);
        }
        return;
      }

      if (type === 'round.groups.updated') {
        const payload = message as {
          roundId?: string;
          groups?: Array<{ groupId?: string; teams?: Array<{ teamId?: string; playerIds?: string[] }> }>;
          playerOrder?: string[];
        };
        if (!payload.roundId) return;
        // Clear pending swap
        if (pendingSwapRequestIdRef.current) {
          pendingSwapRequestIdRef.current = null;
          if (swapAckTimeoutRef.current) { clearTimeout(swapAckTimeoutRef.current); swapAckTimeoutRef.current = null; }
        }
        if (payload.groups) {
          const groups = payload.groups
            .filter((g): g is { groupId: string; teams: Array<{ teamId?: string; playerIds?: string[] }> } => !!g.groupId && Array.isArray(g.teams))
            .map((g) => ({
              groupId: g.groupId,
              teams: (g.teams ?? [])
                .filter((t): t is { teamId: string; playerIds: string[] } => !!t.teamId && Array.isArray(t.playerIds))
                .map((t) => ({ teamId: t.teamId, playerIds: t.playerIds })),
            }));
          const playerOrder = payload.playerOrder ?? [];
          setRoundGroupsByRoundId((prev) => ({
            ...prev,
            [payload.roundId!]: { groups, playerOrder },
          }));
          // Also sync roundPlayerIdsByRoundId from playerOrder
          if (playerOrder.length > 0) {
            setRoundPlayerIdsByRoundId((prev) => ({
              ...prev,
              [payload.roundId!]: playerOrder,
            }));
          }
        }
        return;
      }

      if (type === 'round.groups.swap.rejected') {
        const payload = message as { roundId?: string; message?: string };
        pendingSwapRequestIdRef.current = null;
        if (swapAckTimeoutRef.current) { clearTimeout(swapAckTimeoutRef.current); swapAckTimeoutRef.current = null; }
        setError(payload.message ?? t('tourEdit.saveFailed'));
        // Revert by re-fetching — the next round.groups.updated from backend will correct state
        return;
      }

      if (type === 'round.initiate.rejected') {
        setSaving(false);
        const payload = message as { message?: string };
        setError(payload.message ?? t('tourEdit.roundCreateFailed'));
        return;
      }

      if (type === 'player.temporary.created') {
        const payload = message as { requestId?: string; playerId?: string; name?: string };
        if (payload.playerId && payload.name) {
          setAvailablePlayers((prev) => {
            if (prev.some((p) => p.id === payload.playerId)) return prev;
            return [...prev, { id: payload.playerId!, name: payload.name!, userId: null, isFriend: false }];
          });
          h.sendTourMemberAdd(payload.playerId, 'temporary.created');
        }
        return;
      }
    });

    return () => {
      unsubscribe();
      socket.setSubscriptionContext({ tourId: null });
    };
    // Re-subscribe when token or tourId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token, tourId]);

  // --- Initial data load ---
  const initialLoadRef = useRef(false);
  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    fetchTour();
    fetchRounds();
    fetchGameDefinitions();
    fetchTourPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId, user?.token]);

  // --- Request friend list ---
  useEffect(() => {
    if (!user?.token) {
      friendPlayerIdsRef.current = new Set();
      return;
    }
    socketRef.current.setAuthToken(user.token);
    socketRef.current.sendFriendListRequest(generateId());
  }, [user?.token]);

  // --- Fetch round games, groups, and scores for each round ---
  const fetchedRoundIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    rounds.forEach((round) => {
      if (round.id && !fetchedRoundIdsRef.current.has(round.id)) {
        fetchedRoundIdsRef.current.add(round.id);
        fetchRoundGames(round.id);
        fetchRoundGroups(round.id);
        if (round.status && round.status !== 'draft') {
          fetchRoundScores(round.id);
        }
      }
    });
  }, [rounds, fetchRoundGames, fetchRoundGroups, fetchRoundScores]);

  // --- Cleanup timeouts on unmount ---
  useEffect(() => {
    return () => {
      if (ackTimeoutRef.current) clearTimeout(ackTimeoutRef.current);
      if (memberAddAckTimeoutRef.current) clearTimeout(memberAddAckTimeoutRef.current);
      if (tourRemoveAckTimeoutRef.current) clearTimeout(tourRemoveAckTimeoutRef.current);
      pendingRoundDeletesRef.current.forEach((entry) => clearTimeout(entry.timeout));
      if (roundAddAckTimeoutRef.current) clearTimeout(roundAddAckTimeoutRef.current);
      if (roundRemoveAckTimeoutRef.current) clearTimeout(roundRemoveAckTimeoutRef.current);
      if (swapAckTimeoutRef.current) clearTimeout(swapAckTimeoutRef.current);
      reconcileRefreshTimersRef.current.forEach((timer) => clearTimeout(timer));
      memberAddReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
      tourRemoveReconcileTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return {
    // State
    tourName,
    setTourName,
    isHidden,
    loading,
    saving,
    error,
    setError,
    statusNote,
    gameDefinitions,
    enabledGameDefinitions,
    tournamentGamesState,
    isTournamentManagedGame,
    rounds,
    orderedRounds,
    roundsLoading,
    roundGamesByRoundId,
    roundPlayerIdsByRoundId,
    playerIdsWithScoresByRoundId,
    roundGroupsByRoundId,
    availablePlayers,
    playerRows,
    setPlayerRows,
    playerRowOpen,
    setPlayerRowOpen,
    tourPlayersLoading,
    tourMemberOptions,
    playerNameById,
    courses,
    loadingCourses,
    courseError,

    // Actions
    fetchTour,
    fetchRounds,
    fetchRoundGames,
    fetchTourPlayers,
    fetchCourses,
    sendTourUpdate,
    updateTournamentGames,
    updateTournamentGameWeight,
    sendTourMemberAdd,
    sendTourMemberRemove,
    sendRoundMemberAdd,
    sendRoundMemberRemove,
    sendRoundGroupsSwap,
    sendRoundRemove,
    sendCreateRound,
    sendTemporaryPlayerCreate,
    updateRoundGames,
    updateRoundGameWeight,
    updateRoundSettings,

    // Refs
    socketRef,
    logToBff,
    apiBaseUrl,
  };
}
